
import type * as TrackingTypes from "./trackingTypes"
import { IItem } from "./trackingTypes"

declare global {
  interface Window {
    ojtag: (event: string, data: any) => any
    GTM_CUSTOMER: TrackingTypes.IGTMCustomer
  }
}

declare global {
  interface Window {
    oj: IOJ
  }
}

interface ITrackingModel<T = unknown> {
  items: T[]
}

interface ITrackingEvent {
  event: TrackingTypes.TrackingEvent
}

interface IEventData {
  model: ITrackingModel<IItem>
  list?: string
}

type ImpressionFn = (
  event: TrackingTypes.TrackingEvent,
  model: ITrackingModel<IItem>,
  list: string,
) => void

type ImpressionItemData = Partial<Record<TrackingTypes.TrackingEvent, IEventData>>
type ImpressionListData = (ITrackingEvent & IEventData)[]

interface IConfig {
  intersectionRatio?: number
  debug?: boolean
}

interface IOJBase {
  config?: IConfig
  initialized?: boolean
}

interface IOJInitialized extends IOJBase {
  impressionReview: ImpressionFn
  impressionItem: ImpressionFn
  impressionList: ImpressionFn
  impressionItemData: ImpressionItemData
  impressionListData: ImpressionListData
  observer: IntersectionObserver
}

type IOJ = IOJInitialized

const attributes = [
  "data-onviewport",
  "data-onviewportintersection"
]

const [onviewport, onviewportIntersection] = attributes

const initialize = (config?: IConfig) => {

  const impressionReview: ImpressionFn = (event, model) => {
    window.ojtag(event, model)
  }

  const impressionItemData: ImpressionItemData = {}
  const impressionItem: ImpressionFn = (event, model, listId) => {
    event in impressionItemData
      ? impressionItemData[event]!.model.items.push(...model.items)
      : (impressionItemData[event] = { model, list: listId })

    if (config?.debug)
      console.log("Got impression item", impressionItemData)
  }

  const impressionListData: ImpressionListData = []
  const impressionList: ImpressionFn = (event, model, listId?) => {
    if (!impressionListData.find(x => x.list === listId))
      impressionListData.push({ event, model, list: listId })

    if (config?.debug)
      console.log("Got impression list", impressionListData)
  }

  const observer = new IntersectionObserver(
    (entries: IntersectionObserverEntry[], observer: IntersectionObserver) =>
      entries
        .filter(({ isIntersecting }) => isIntersecting)
        .forEach(({ target, intersectionRatio }) => {
          const vpRatio = target.getAttribute(onviewport)!
          const vpInter = target.getAttribute(onviewportIntersection)!

          if (vpInter || (vpRatio && intersectionRatio >= (config?.intersectionRatio ?? 0.5))) {
            const vp = vpInter || vpRatio

            try {
              new Function(`'use strict'; ${vp};`)()
            } catch (err) {
              console.error(err, `func: ${vp}`)
            }

            observer.unobserve(target)
          }
        }),
    {
      threshold: [0, (config?.intersectionRatio ?? 0.5)],
    }
  )

  window.addEventListener("load", () => {
    const observeOnViewportElements = (node: Document | Element) =>
      node.querySelectorAll(attributes.map(x => `[${x}]`).join(", ")).forEach((x) => observer.observe(x))

    new MutationObserver((entries) =>
      entries
        .filter(({ target, type }) => type === "childList" && target instanceof HTMLElement)
        .forEach(({ target }) => observeOnViewportElements(target as HTMLElement))
    ).observe(document.body, { childList: true, subtree: true })

    observeOnViewportElements(document)
  })

  window.addEventListener("beforeunload", () => {
    const values = Object.values(impressionItemData)
    const data = impressionListData
      .map((x) => ({
        ...x,
        model: {
          ...x.model,
          items: values
            .flatMap((x) => x.model.items)
            .filter(y => y.item_list_name === x.list)
        },
      }))

    data.forEach((x) => window.ojtag(x.event, x.model))

    if (config?.debug) {
      console.log("Impression data", data)
      return "Impression data send";
    }
  })

  return {
    initialized: true,
    impressionReview,
    impressionItem,
    impressionItemData,
    impressionList,
    impressionListData,
    observer,
    config
  }
}

try {
  if (!window.oj?.initialized) window.oj = initialize(window.oj.config)
}
catch (e) {
  console.error(`[OJ Tracking]`, e)
}

window.addEventListener('CookiebotOnAccept', function (e) {
  if (localStorage.getItem("cookieConsentStatus"))
    return

  const consent = window["Cookiebot"]?.consent;
  const statusArray = [consent.marketing, consent.necessary, consent.preferences, consent.statistics]
  const status = statusArray.every(x => x) ? '1' : '0';

  if (status === "1") {
    window["dataLayer"].push({
      'event': 'acceptCookies',
      'cookieConsentStatus': status
    });
  } else if (status === "0") {
    window["dataLayer"].push({
      'event': 'refuseCookies',
      'cookieConsentStatus': status
    });
  }

  localStorage.setItem("cookieConsentStatus", status)
}, false);

// window.addEventListener('CookiebotOnDecline', function (e) {
//   window["dataLayer"].push({
//     'event': 'refuseCookies',
//     'cookieConsentStatus': '0'
//   });

//   localStorage.setItem("cookieConsentStatus", "0")
// }, false);


